import React, { useEffect } from 'react';

// ─── Posted systemKit ─────────────────────────────────────────────────────────
// Shared UI primitives for the Operations sub-tab family. Every primitive here
// enforces a rule from DESIGN_SYSTEM.md. Usage:
//   import { KpiCard, DateStepper, SegmentedPills } from '../components/systemKit';
//
// Named exports only. No default, no barrel re-exports. Grow this file as new
// primitives are needed — don't pre-emptively add props "just in case".
//
// Dark-mode discipline: every surface / border / text inside these primitives
// MUST reference CSS vars from src/index.css. No bg-white / text-gray-*
// Tailwind literals here (those get dark-mapped in page code, not in the
// primitives themselves — primitives ship dark-mode-correct by default).

// ─────────────────────────────────────────────────────────────────────────────
/**
 * SegmentedPills — per DESIGN_SYSTEM.md §Tabs > Segmented pills (canonical)
 *
 * Container: var(--bg-hover) bg, 1px var(--border), 6px radius, 2px inner pad.
 * Inactive pill: transparent bg, var(--text-secondary), 14/500, 6px radius,
 *   6/12 padding.
 * Active pill: var(--bg-card) bg, var(--text-primary), subtle shadow.
 * Hover (inactive): text → var(--text-primary).
 *
 * Props:
 *   value: any (the current selection key)
 *   onChange: (key) => void
 *   options: [{ key, label, title?, count?, disabled?, disabledTitle? }]
 *     - count renders a small numeric chip after the label when > 0
 *     - disabled renders the pill in a muted, not-clickable state.
 *       Used by surfaces (e.g. drill-in metric toggle) where some
 *       options exist conceptually but have no data to render.
 *     - disabledTitle overrides title for the disabled-state tooltip
 *       so the operator gets "No data on this metric for this item"
 *       rather than the active-state title.
 *
 * Usage:
 *   <SegmentedPills value={tab} onChange={setTab}
 *     options={[
 *       { key: 'week', label: 'Week' },
 *       { key: 'range', label: 'Range' },
 *     ]}
 *   />
 */
export function SegmentedPills({ value, onChange, options, size = 'md' }) {
  const pad = size === 'sm' ? '4px 10px' : '6px 12px';
  const fontSize = size === 'sm' ? 13 : 14;
  return (
    <div
      className="inline-flex no-print"
      style={{ background: 'var(--bg-hover)', border: '1px solid var(--border)', borderRadius: 6, padding: 2 }}
    >
      {options.map(opt => {
        const active = value === opt.key;
        const disabled = !!opt.disabled;
        const tooltip = disabled && opt.disabledTitle ? opt.disabledTitle : opt.title;
        return (
          <button
            key={opt.key}
            title={tooltip}
            onClick={() => { if (!disabled) onChange(opt.key); }}
            disabled={disabled}
            aria-disabled={disabled || undefined}
            className="transition-colors font-medium inline-flex items-center gap-1"
            style={{
              background: active ? 'var(--bg-card)' : 'transparent',
              color: disabled
                ? 'var(--text-muted)'
                : active
                  ? 'var(--text-primary)'
                  : 'var(--text-secondary)',
              borderRadius: 6,
              padding: pad,
              fontSize,
              border: 'none',
              cursor: disabled ? 'not-allowed' : 'pointer',
              boxShadow: active ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
              whiteSpace: 'nowrap',
              opacity: disabled ? 0.6 : 1,
            }}
            onMouseEnter={e => { if (!active && !disabled) e.currentTarget.style.color = 'var(--text-primary)'; }}
            onMouseLeave={e => { if (!active && !disabled) e.currentTarget.style.color = 'var(--text-secondary)'; }}
          >
            {opt.label ?? opt.key}
            {opt.count != null && opt.count > 0 && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                  background: active ? 'var(--bg-hover)' : 'var(--bg-subtle)',
                  borderRadius: 999,
                  padding: '1px 6px',
                  lineHeight: 1.4,
                }}
              >
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
/**
 * StatusPill — per DESIGN_SYSTEM.md §Badges / pills
 *
 * 6px radius, 11/500, 2/8 padding, light functional bg + matching dark text.
 * Standardized at 11/500. Micro-context labels below this size (10px "auto"
 * tags etc.) should render as inline text with var(--text-muted), NOT a
 * shrunk StatusPill — don't pollute this API with size variants.
 *
 * Tones: green / amber / red / blue / purple / neutral.
 *
 * Props:
 *   tone: one of the tones above (default 'neutral')
 *   children: label content
 */
export function StatusPill({ tone = 'neutral', children }) {
  const palette = {
    green:   { background: 'var(--bg-success-subtle)',  color: 'var(--text-success)' },
    amber:   { background: 'var(--bg-warning-subtle)',  color: 'var(--text-warning)' },
    red:     { background: 'var(--bg-critical-subtle)', color: 'var(--text-critical)' },
    blue:    { background: 'var(--bg-info-subtle)',     color: 'var(--text-info)' },
    purple:  { background: 'var(--bg-accent-chip)',     color: 'var(--accent-primary)' },
    neutral: { background: 'var(--bg-subtle)',          color: 'var(--text-secondary)' },
  }[tone] || { background: 'var(--bg-subtle)', color: 'var(--text-secondary)' };
  return (
    <span
      className="inline-flex items-center whitespace-nowrap"
      style={{ background: palette.background, color: palette.color, padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 500 }}
    >
      {children}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
/**
 * Button variants — per DESIGN_SYSTEM.md §Buttons
 *
 * All buttons: 8px radius, 8px 14px padding, 14/500, transition-colors.
 * Each variant is exported separately so callers can't pick a wrong mix.
 *
 * Primary (black)       — routine approvals / commits / saves. Default.
 * PrimaryAccent (purple) — high-stakes confirmations that fan out
 *                           (SMS, team notifications, permanent changes).
 * Secondary (outlined)  — supporting actions. Cancel, secondary CTAs,
 *                           filter toggles rendered as buttons.
 * Destructive           — red-outlined. Delete, decline, clear.
 *
 * Props (all variants):
 *   onClick, disabled, children, className, title, type
 */
export function PrimaryBtn({ onClick, disabled, children, className = '', title, type = 'button' }) {
  return (
    <button
      type={type}
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`text-sm font-medium transition-colors disabled:opacity-50 ${className}`}
      style={{ background: 'var(--text-primary)', color: 'var(--bg-card)', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer' }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = '#262626'; }}
      onMouseLeave={e => { if (!disabled) e.currentTarget.style.background = 'var(--text-primary)'; }}
    >
      {children}
    </button>
  );
}

export function PrimaryAccentBtn({ onClick, disabled, children, className = '', title, type = 'button' }) {
  return (
    <button
      type={type}
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`text-sm font-medium transition-colors disabled:opacity-50 ${className}`}
      style={{ background: 'var(--accent-primary)', color: '#FFFFFF', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer' }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = 'var(--accent-primary-hover)'; }}
      onMouseLeave={e => { if (!disabled) e.currentTarget.style.background = 'var(--accent-primary)'; }}
    >
      {children}
    </button>
  );
}

export function SecondaryBtn({ onClick, disabled, children, className = '', title, type = 'button' }) {
  return (
    <button
      type={type}
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`text-sm font-medium transition-colors disabled:opacity-50 ${className}`}
      style={{ background: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border)', padding: '8px 14px', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer' }}
      onMouseEnter={e => { if (!disabled) { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.borderColor = 'var(--border-strong)'; } }}
      onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-card)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      {children}
    </button>
  );
}

export function DestructiveBtn({ onClick, disabled, children, className = '', title, type = 'button' }) {
  return (
    <button
      type={type}
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`text-sm font-medium transition-colors disabled:opacity-50 ${className}`}
      style={{ background: 'var(--bg-card)', color: 'var(--text-critical)', border: '1px solid var(--text-critical)', padding: '8px 14px', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer' }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = 'var(--bg-critical-subtle)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-card)'; }}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
/**
 * KpiCard — per DESIGN_SYSTEM.md §Cards + §Typography Display
 *
 * 8px radius, 1px var(--border), 20px padding desktop / 16px mobile,
 * no shadow, var(--bg-card) background.
 *
 * Typography: 12/500 uppercase tracking-wide var(--text-secondary) label,
 * 32/700 tracking-tight var(--text-primary) number.
 *
 * Meaning-driven variant (per §Cards > Meaning-driven KPI card variants):
 * a card that conveys a state (under target / over target / needs attention)
 * uses a 3px colored left accent bar + the matching --bg-*-subtle fill at
 * ~40% opacity. The rest of the border stays var(--border). Full-color
 * borders read as urgent/error and should be avoided.
 *
 * Props:
 *   label: string
 *   value: string | number
 *   secondary?: string     — caption under the value (13/400 var(--text-secondary))
 *   valueTone?: 'success' | 'warning' | 'critical' | 'neutral'
 *                           — colors the NUMBER itself for inline meaning-on-value
 *   meaning?:  'success' | 'warning' | 'critical'
 *                           — whole-card accent: left bar + subtle tint
 *   delta?: { direction: 'up' | 'down', value: string, tone: 'success' | 'critical' | 'neutral' }
 *   onClick?: () => void    — when provided, card becomes a button with hover border
 *
 * Usage:
 *   <KpiCard label="Labor Cost" value="28.9%" secondary="$10.3k (target 25%)"
 *            valueTone="warning" />
 *   <KpiCard label="Food Cost" value="27.4%" secondary="$3,331 (target 30%)"
 *            meaning="success" />
 */
const MEANING_PALETTE = {
  success:  { bar: 'var(--text-success)',  tint: 'var(--bg-success-subtle)' },
  warning:  { bar: 'var(--text-warning)',  tint: 'var(--bg-warning-subtle)' },
  critical: { bar: 'var(--text-critical)', tint: 'var(--bg-critical-subtle)' },
};

const VALUE_TONE_COLOR = {
  success:  'var(--text-success)',
  warning:  'var(--text-warning)',
  critical: 'var(--text-critical)',
  neutral:  'var(--text-muted)',
};

export function KpiCard({ label, value, secondary, valueTone, meaning, delta, onClick }) {
  const clickable = typeof onClick === 'function';
  const meta = meaning ? MEANING_PALETTE[meaning] : null;
  const valueColor = valueTone ? VALUE_TONE_COLOR[valueTone] : 'var(--text-primary)';
  const Tag = clickable ? 'button' : 'div';

  return (
    <Tag
      onClick={clickable ? onClick : undefined}
      className={`text-left rounded-lg transition-colors w-full ${clickable ? 'cursor-pointer' : ''}`}
      style={{
        // Card shell per spec; meaning adds a left accent bar via 3px left border +
        // a soft tint. The rest of the border stays var(--border).
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderLeft: meta ? `3px solid ${meta.bar}` : '1px solid var(--border)',
        padding: '20px',
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseEnter={clickable ? e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; } : undefined}
      onMouseLeave={clickable ? e => { e.currentTarget.style.borderColor = 'var(--border)'; } : undefined}
    >
      {/* Tint layer for meaning — ~40% of the functional bg-*-subtle color. Sits
          behind content; kept as an absolute layer so it doesn't affect padding. */}
      {meta && (
        <span
          aria-hidden
          style={{ position: 'absolute', inset: 0, background: meta.tint, opacity: 0.4, pointerEvents: 'none' }}
        />
      )}
      <div style={{ position: 'relative' }}>
        <div
          style={{ fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 8 }}
        >
          {label}
        </div>
        <div className="flex items-baseline gap-2 flex-wrap">
          <div
            style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1, color: valueColor }}
          >
            {value}
          </div>
          {delta && (
            <span
              className="inline-flex items-center gap-0.5"
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: VALUE_TONE_COLOR[delta.tone] || 'var(--text-secondary)',
              }}
            >
              <span>{delta.direction === 'up' ? '↑' : delta.direction === 'down' ? '↓' : ''}</span>
              <span>{delta.value}</span>
            </span>
          )}
        </div>
        {secondary && (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>
            {secondary}
          </div>
        )}
      </div>
    </Tag>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Period-aware DateStepper utilities. Calendar-based windows; week starts Sunday.
// Exported so consumer pages (Inventory Trends) can reuse the same math for
// vs-prior comparisons.
//
// Period scale, used for period-switch direction:
//   week (1) < month (2) < quarter (3) < year (4)
// Switching to a SMALLER period = zoom in → snap to period containing today.
// Switching to a LARGER period = zoom out → snap to period containing the
// current range's start date. Same scale = no-op.

const PERIOD_SCALE = { week: 1, month: 2, quarter: 3, year: 4 };

function toIsoDate(d) {
  // Local-tz YYYY-MM-DD (avoids the off-by-one drift toISOString().split('T')[0]
  // produces in negative-UTC-offset timezones).
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function asDate(v) {
  if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
  // ISO string YYYY-MM-DD; parse explicitly to avoid TZ shenanigans.
  const [y, m, d] = String(v).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

/**
 * rangeForPeriod — returns { start, end } as ISO date strings (local tz) for
 * the calendar period containing the anchor date.
 *
 *   week    Sunday → Saturday containing anchor
 *   month   1st → last day of anchor's calendar month
 *   quarter Q1 = Jan-Mar, Q2 = Apr-Jun, Q3 = Jul-Sep, Q4 = Oct-Dec
 *   year    Jan 1 → Dec 31 of anchor's year
 */
export function rangeForPeriod(period, anchor = new Date()) {
  const a = asDate(anchor);
  if (period === 'week') {
    const day = a.getDay(); // 0 = Sunday
    const start = new Date(a); start.setDate(a.getDate() - day);
    const end = new Date(start); end.setDate(start.getDate() + 6);
    return { start: toIsoDate(start), end: toIsoDate(end) };
  }
  if (period === 'month') {
    const start = new Date(a.getFullYear(), a.getMonth(), 1);
    const end = new Date(a.getFullYear(), a.getMonth() + 1, 0);
    return { start: toIsoDate(start), end: toIsoDate(end) };
  }
  if (period === 'quarter') {
    const q = Math.floor(a.getMonth() / 3); // 0..3
    const start = new Date(a.getFullYear(), q * 3, 1);
    const end = new Date(a.getFullYear(), q * 3 + 3, 0);
    return { start: toIsoDate(start), end: toIsoDate(end) };
  }
  // year
  const start = new Date(a.getFullYear(), 0, 1);
  const end = new Date(a.getFullYear(), 11, 31);
  return { start: toIsoDate(start), end: toIsoDate(end) };
}

/**
 * priorPeriodRange — calendar-based prior period (NOT same-length-immediately-
 * preceding). Prior week of Apr 19–25 is Apr 12–18; prior month of Apr 2026 is
 * Mar 2026; prior quarter of Q2 2026 is Q1 2026; prior year of 2026 is 2025.
 */
export function priorPeriodRange(period, currentRange) {
  const start = asDate(currentRange.start);
  if (period === 'week') {
    const a = new Date(start); a.setDate(start.getDate() - 7);
    return rangeForPeriod('week', a);
  }
  if (period === 'month') {
    const a = new Date(start.getFullYear(), start.getMonth() - 1, 1);
    return rangeForPeriod('month', a);
  }
  if (period === 'quarter') {
    const a = new Date(start.getFullYear(), start.getMonth() - 3, 1);
    return rangeForPeriod('quarter', a);
  }
  // year
  const a = new Date(start.getFullYear() - 1, 0, 1);
  return rangeForPeriod('year', a);
}

function stepPeriod(period, currentRange, direction) {
  const start = asDate(currentRange.start);
  if (period === 'week') {
    const a = new Date(start); a.setDate(start.getDate() + 7 * direction);
    return rangeForPeriod('week', a);
  }
  if (period === 'month') {
    const a = new Date(start.getFullYear(), start.getMonth() + direction, 1);
    return rangeForPeriod('month', a);
  }
  if (period === 'quarter') {
    const a = new Date(start.getFullYear(), start.getMonth() + 3 * direction, 1);
    return rangeForPeriod('quarter', a);
  }
  const a = new Date(start.getFullYear() + direction, 0, 1);
  return rangeForPeriod('year', a);
}

/**
 * Period-switch range: zoom-in (smaller scale) snaps to the period containing
 * today; zoom-out (larger scale) snaps to the period containing the current
 * range's start. See DESIGN_SYSTEM.md §Period-aware DateStepper.
 */
export function rangeForPeriodSwitch(newPeriod, oldPeriod, currentRange) {
  if (PERIOD_SCALE[newPeriod] === PERIOD_SCALE[oldPeriod]) {
    return rangeForPeriod(newPeriod, asDate(currentRange.start));
  }
  if (PERIOD_SCALE[newPeriod] < PERIOD_SCALE[oldPeriod]) {
    return rangeForPeriod(newPeriod, new Date()); // zoom in → today-aware
  }
  return rangeForPeriod(newPeriod, asDate(currentRange.start)); // zoom out → start-of-range
}

function formatPeriodLabel(period, range) {
  const start = asDate(range.start);
  const end = asDate(range.end);
  if (period === 'week') {
    const opt = { month: 'short', day: 'numeric' };
    return `${start.toLocaleDateString('en-US', opt)} \u2013 ${end.toLocaleDateString('en-US', { ...opt, year: 'numeric' })}`;
  }
  if (period === 'month') return start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  if (period === 'quarter') return `Q${Math.floor(start.getMonth() / 3) + 1} ${start.getFullYear()}`;
  return String(start.getFullYear());
}

/**
 * DateStepper — period-aware navigation control.
 *
 * MODES
 *   1. Period-aware (NEW): pass `period`, `onPeriodChange`, `range`,
 *      `onRangeChange`. Renders a SegmentedPills row above the stepper.
 *      Reset label is hardcoded "Today" — snaps to current period.
 *   2. Single-mode (legacy): pass `label`, `onPrev`, `onNext`, optional
 *      `onReset` + `resetLabel`. Renders the stepper only. Existing
 *      consumers continue to work unchanged.
 *
 * LAYOUT FIX (both modes)
 *   Label is sized to content (no minWidth, no text-center). Cluster reads
 *   tightly — fixes the prior bug where a centered fixed-width label put
 *   ~270px of empty space between the back arrow and the date.
 *
 * Props:
 *   period?: 'week' | 'month' | 'quarter' | 'year'
 *   onPeriodChange?: (next) => void
 *   range?: { start: ISOString, end: ISOString }
 *   onRangeChange?: ({ start, end }) => void
 *   periods?: Array<period>            — defaults to all four
 *   alignment?: 'left' | 'right'       — defaults to 'right'
 *   weekStart?: 'sunday' | 'monday'    — currently 'sunday' only (configurable later)
 *   label?: string                     — single-mode only
 *   onPrev?, onNext?, onReset?         — single-mode only
 *   resetLabel?: string                — single-mode only; default 'Today'
 *
 * See DESIGN_SYSTEM.md §Period-aware DateStepper.
 */
export function DateStepper({
  // period-aware
  period, onPeriodChange, range, onRangeChange,
  periods = ['week', 'month', 'quarter', 'year'],
  alignment = 'right',
  // single-mode
  label, onPrev, onNext, onReset, resetLabel = 'Today',
}) {
  const periodMode = !!period;

  // Period-aware handlers
  const handlePrev = periodMode
    ? () => onRangeChange?.(stepPeriod(period, range, -1))
    : onPrev;
  const handleNext = periodMode
    ? () => onRangeChange?.(stepPeriod(period, range, +1))
    : onNext;
  const handleReset = periodMode
    ? () => onRangeChange?.(rangeForPeriod(period, new Date()))
    : onReset;
  const computedLabel = periodMode ? formatPeriodLabel(period, range) : label;
  const computedResetLabel = periodMode ? 'Today' : resetLabel;

  const alignClass = alignment === 'left' ? 'items-start' : 'items-end';

  const stepperRow = (
    <div className="flex items-center gap-2 no-print">
      <GhostIconBtn onClick={handlePrev} ariaLabel="Previous">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
      </GhostIconBtn>
      {/* Label sized to content. NO minWidth, NO text-center — that combo was the
          source of the ~270px-gap layout bug in the prior version. */}
      <div
        className="text-sm font-medium whitespace-nowrap"
        style={{ color: 'var(--text-primary)', padding: '0 8px' }}
      >
        {computedLabel}
      </div>
      <GhostIconBtn onClick={handleNext} ariaLabel="Next">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
      </GhostIconBtn>
      {handleReset && (
        <SecondaryBtn onClick={handleReset} className="!px-3 !py-1.5 !text-xs">
          {computedResetLabel}
        </SecondaryBtn>
      )}
    </div>
  );

  if (!periodMode) return stepperRow;

  const pillOptions = periods.map(p => ({
    key: p,
    label: p === 'week' ? 'Week' : p === 'month' ? 'Month' : p === 'quarter' ? 'Quarter' : 'Year',
  }));

  return (
    <div className={`flex flex-col gap-2 ${alignClass}`}>
      <SegmentedPills
        value={period}
        onChange={(next) => {
          if (next === period) return;
          // Compute the new range BEFORE flipping the period, so range-derived
          // logic on the next render is consistent.
          const nextRange = rangeForPeriodSwitch(next, period, range);
          onRangeChange?.(nextRange);
          onPeriodChange?.(next);
        }}
        options={pillOptions}
        size="sm"
      />
      {stepperRow}
    </div>
  );
}

// Ghost icon button — internal helper for DateStepper arrows.
function GhostIconBtn({ onClick, ariaLabel, children }) {
  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      className="flex items-center justify-center rounded-md transition-colors"
      style={{ width: 32, height: 32, background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)', cursor: 'pointer' }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
/**
 * ChartCard — shared wrapper for the 5+ SVG chart cards across Operations.
 *
 * Card shell per §Cards (8px radius, 1px var(--border), var(--bg-card),
 * no shadow). Title uses §Typography H2 (14/600). Optional subtitle below
 * title in 12/400 var(--text-muted). Optional `legend` slot renders to
 * the right of the title on a single line, wraps below on narrow.
 *
 * Chart CHROME (axis text, gridlines, tooltip surface) must use CSS vars:
 *   gridline stroke  → var(--border)
 *   axis label fill  → var(--text-muted)
 *   tooltip bg       → var(--bg-elevated)
 *   tooltip border   → var(--border)
 * Chart DATA SERIES (line / bar colors) stay hardcoded per Data Colors rule.
 *
 * Props:
 *   title: string
 *   subtitle?: string
 *   legend?: ReactNode
 *   padding?: string (default "20px")
 *   children: ReactNode (the SVG or an empty-state placeholder)
 */
export function ChartCard({ title, subtitle, legend, padding = '20px', children }) {
  return (
    <div
      className="rounded-lg"
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', padding }}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</h2>
          {subtitle && (
            <p className="mt-0.5" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{subtitle}</p>
          )}
        </div>
        {legend && (
          <div className="flex items-center gap-3 flex-wrap" style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
            {legend}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
/**
 * Sparkline — generic inline micro-chart for table rows.
 *
 * Replaces the price-specific sparkline in src/components/price/Sparkline.js.
 * Both Price and Volume views need sparklines with opposite directional semantics:
 *   - Price: up is BAD (vendor raising prices) → red; down is good → green
 *   - Volume: up is neutral-to-good (ordering more) → green by default; down is red
 * The `tone` prop selects the color mapping. 'neutral' is a single-color chart
 * regardless of direction — use it when up/down doesn't carry a value judgement.
 *
 * Props:
 *   data: number[] (required). Plain numeric array. Callers extract their field
 *     of interest (price_per_lb, case_price, quantity, lbs) before passing in.
 *   width?: number   (default 100)
 *   height?: number  (default 28)
 *   tone?: 'price' | 'volume' | 'neutral'   (default 'neutral')
 *   flatThreshold?: number   (default 0.01 — relative spread; also 0.01 absolute)
 *
 * Flat detection is deliberate: float drift at <1% variance looks like a trend
 * line, which is misleading. Anything below the threshold renders flat + gray.
 *
 * Data colors per §Data Colors — these are content signals, not UI chrome.
 */
export function Sparkline({ data, width = 100, height = 28, tone = 'neutral', flatThreshold = 0.01 }) {
  if (!Array.isArray(data) || data.length < 2) return null;
  const values = data.filter(v => v != null && isFinite(Number(v))).map(Number);
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const median = values.slice().sort((a, b) => a - b)[Math.floor(values.length / 2)];
  const absSpread = max - min;
  const relSpread = median > 0 ? absSpread / median : 0;
  const flat = absSpread < flatThreshold || relSpread < flatThreshold;
  const range = flat ? 1 : absSpread;
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = flat ? height / 2 : height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  const trendingUp = values[values.length - 1] > values[0];
  let color = '#9CA3AF';
  if (!flat) {
    if (tone === 'price')  color = trendingUp ? '#DC2626' : '#2D8F5E'; // price up = red
    if (tone === 'volume') color = trendingUp ? '#2D8F5E' : '#DC2626'; // volume up = green
    if (tone === 'neutral') color = '#6D28D9'; // brand accent for neutral trends
  }
  return (
    <svg width={width} height={height} className="inline-block">
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={points} />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
/**
 * SlideOverPanel — right-side slide-in drawer for item detail / drill-down views.
 *
 * Responsive behavior: 480px wide on desktop (≥640px), full-screen modal below
 * that. Closes on ESC key, backdrop click, or the close button. Renders via
 * a fixed-position overlay; does NOT use React portals (keeps it simple and
 * within the existing stacking context — matches the LaborCosts drill-down
 * pattern that proved out in Commit 4 of the Operations redesign).
 *
 * Props:
 *   open: boolean         — controlled open state
 *   onClose: () => void   — fires on backdrop click, ESC, or close button
 *   title?: ReactNode     — rendered in the drawer header
 *   subtitle?: ReactNode  — smaller text under the title
 *   children: ReactNode   — drawer body content
 *   widthPx?: number      — desktop width (default 480)
 *
 * Dark-mode discipline: all surfaces/borders reference CSS vars.
 */
export function SlideOverPanel({ open, onClose, title, subtitle, children, widthPx = 480 }) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop — also closes the drawer on click. Dimmer, not a solid panel. */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.4)', zIndex: 50,
        }}
      />
      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: '100%', maxWidth: widthPx,
          background: 'var(--bg-card)',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
          zIndex: 51,
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div
          className="px-5 py-4 flex items-start justify-between gap-3"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <div className="min-w-0 flex-1">
            {title && <div className="text-base font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{title}</div>}
            {subtitle && <div className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>{subtitle}</div>}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-lg px-1 rounded-md transition-colors"
            style={{ color: 'var(--text-secondary)', background: 'transparent', border: 'none', cursor: 'pointer' }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'transparent'; }}
          >
            {'\u2715'}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </div>
    </>
  );
}

// ─── CollapsibleSection ─────────────────────────────────────────────────────
//
// Generic glance-then-drill content card. Shared shape used by Vendor Watch
// and Data Review on the Price Tracker page (and any future "queue / list
// of things needing attention" surface).
//
// Pattern:
//   ┌─ header ──────────────────────────────────────────────────────┐
//   │ [icon] Title · [count chip]              [actions]   [▾ caret] │
//   │ subhead text — "Beef Brisket +13% · Olive Oil +17% · ..."     │
//   ├─ body (visible when expanded) ────────────────────────────────┤
//   │ children                                                      │
//   └────────────────────────────────────────────────────────────────┘
//
// The whole header row is a real <button> — ENTER + SPACE toggle, focus
// outline, aria-expanded reflects state. Chevron rotates 0deg → 180deg
// on expand at 200ms ease, matching the sidebar group convention.
//
// State persistence: sessionStorage keyed by `posted-collapse:${id}`.
// Session-only on purpose — operators want fresh context per page visit
// (same convention as sidebar group expansion in App.js). Pass a stable
// `id` per surface; collisions silently corrupt cross-section state.
//
// Empty state: when `empty` is truthy, the section renders a muted,
// non-clickable header — no chevron, no body, but the section is still
// in the DOM so the page layout doesn't shift when items appear.
//
// Loading state: when `loading` is truthy, the subhead renders
// "checking…" italic and the body stays collapsed. Useful for the
// first-paint window before data resolves.
//
// Action slot: optional `headerAction` ReactNode rendered to the right
// of the title block, before the chevron. Use it for surface-level
// controls that should be reachable without expanding (e.g. Vendor
// Watch's "Run alert check" link). Click on the action stops
// propagation so it doesn't toggle the section.
//
// See DESIGN_SYSTEM.md §Collapsible content sections.

const COLLAPSE_STORAGE_PREFIX = 'posted-collapse:';

function readCollapseState(id, fallback) {
  if (typeof window === 'undefined' || !id) return fallback;
  try {
    const raw = window.sessionStorage.getItem(COLLAPSE_STORAGE_PREFIX + id);
    if (raw === '1') return true;
    if (raw === '0') return false;
  } catch (_) { /* sessionStorage may be disabled — fall through */ }
  return fallback;
}

function writeCollapseState(id, expanded) {
  if (typeof window === 'undefined' || !id) return;
  try {
    window.sessionStorage.setItem(COLLAPSE_STORAGE_PREFIX + id, expanded ? '1' : '0');
  } catch (_) { /* ignore */ }
}

export function CollapsibleSection({
  id,
  icon,
  title,
  count,
  countTone = 'amber',
  subhead,
  headerAction,
  defaultExpanded = false,
  loading = false,
  empty = false,
  children,
  className = '',
}) {
  const [expanded, setExpanded] = React.useState(() => readCollapseState(id, defaultExpanded));

  // Track expanded externally only after mount, so SSR/hydration matches.
  React.useEffect(() => {
    writeCollapseState(id, expanded);
  }, [id, expanded]);

  // Empty-state collapses everything: muted header, no chevron, not
  // clickable, no body. Section stays in DOM so layout is stable.
  if (empty) {
    return (
      <div
        className={`mb-4 rounded-lg ${className}`.trim()}
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', padding: '14px 18px' }}
      >
        <div className="flex items-center gap-2" style={{ color: 'var(--text-muted)' }}>
          {icon && <span style={{ color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center' }}>{icon}</span>}
          <span style={{ fontSize: 14, fontWeight: 500 }}>{title}</span>
          <span style={{ fontSize: 13, opacity: 0.85 }}>{empty === true ? '— nothing to review' : `— ${empty}`}</span>
        </div>
      </div>
    );
  }

  const headerId = id ? `collapse-header-${id}` : undefined;
  const bodyId = id ? `collapse-body-${id}` : undefined;

  return (
    <div
      className={`mb-4 rounded-lg ${className}`.trim()}
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
    >
      <button
        id={headerId}
        type="button"
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
        aria-controls={bodyId}
        className="w-full text-left"
        style={{
          background: 'transparent',
          border: 'none',
          padding: '14px 18px',
          cursor: 'pointer',
          display: 'block',
          borderRadius: 8,
        }}
      >
        <div className="flex items-center gap-2 flex-wrap">
          {icon && (
            <span style={{ color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center' }} aria-hidden>
              {icon}
            </span>
          )}
          <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)', margin: 0 }}>
            {title}
          </h2>
          {count != null && count !== '' && (
            <StatusPill tone={countTone}>{count}</StatusPill>
          )}
          <div className="flex-1" />
          {headerAction && (
            // stopPropagation so clicks on the action don't toggle the
            // section. Wrap in a span — the action itself is the
            // operator's interactive element.
            <span
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }}
              style={{ display: 'inline-flex', alignItems: 'center' }}
            >
              {headerAction}
            </span>
          )}
          <svg
            width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5"
            aria-hidden
            style={{
              color: 'var(--text-secondary)',
              transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 200ms ease',
              flexShrink: 0,
            }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
        {(subhead || loading) && (
          <div
            className="mt-1.5"
            style={{
              fontSize: 13,
              color: 'var(--text-secondary)',
              lineHeight: 1.5,
              overflow: 'hidden',
              textOverflow: 'clip',
              whiteSpace: 'nowrap',
            }}
          >
            {loading
              ? <span style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>{'checking\u2026'}</span>
              : subhead}
          </div>
        )}
      </button>
      {expanded && !loading && (
        <div
          id={bodyId}
          role="region"
          aria-labelledby={headerId}
          style={{ padding: '4px 18px 18px 18px' }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Toast (auto-dismiss) ───────────────────────────────────────────────────
// Lightweight notification primitive. Used by Invoices (parser feedback),
// Recipe Health (linkage state-machine transitions), and the live invoice-
// parse alert (top-right, RichToast variant). All expect to fire
// "thing happened, here's the receipt" after a successful mutation OR a
// realtime push from the server.
//
// ── Two-position convention ───────────────────────────────────────────────
// `position: 'bottom-right'` (default — preserves all existing call sites)
//   Confirmations of operator-initiated actions. The user just clicked
//   "Link", "Save", "Delete" — the toast is the receipt. Bottom-right
//   keeps it out of their reading flow.
//
// `position: 'top-right'`
//   Live system events the operator should notice immediately. The user
//   did NOT initiate this — Stripe Reader hub printed an invoice, the
//   parser finished, etc. Top-right is in their peripheral vision so they
//   actually catch it.
//
// See DESIGN_SYSTEM.md §Toast positions for the full convention.
//
// ── Variants ──────────────────────────────────────────────────────────────
// <Toast> — single-line text, kind-tinted left border. The original.
//   API: { toast: { kind?, text }, onDismiss, position? }
//   Used by: Invoices parser feedback, Recipe Health, every existing site.
//
// <RichToast> — multi-line with eyebrow + headline + detail + optional CTA.
//   Used by: live invoice-parse notification (Operations Overview, Home,
//   anywhere App.js fires the realtime handler).
//   API: { toast: { tone, eyebrow, headline, detail, ctaText, onCta },
//          onDismiss, position?, dismissMs? }
//
// <ToastQueue> — stacks multiple toasts with 8px gap. The live-parse
//   notification can fire repeatedly; we don't want each new toast to
//   overwrite the previous (operator might still be reading it).
//   API: { toasts: [...], onDismiss: (id) => void, position?, render? }
//
// ── useToastAutoDismiss ───────────────────────────────────────────────────
// Hook that schedules a setToast(null) after dismissMs. Default 3s. The
// live-parse RichToast overrides to 8s (12s when class === 'pack_changed'
// — that's the killer-feature visual we want operators to read).
//
// .no-print on every toast surface so they don't appear in printouts.

const TOAST_DEFAULT_DISMISS_MS = 3000;
export const TOAST_LIVE_DEFAULT_MS = 8000;
export const TOAST_LIVE_PACK_MS = 12000;

export function useToastAutoDismiss(toast, setToast, dismissMs = TOAST_DEFAULT_DISMISS_MS) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), dismissMs);
    return () => clearTimeout(t);
  }, [toast, setToast, dismissMs]);
}

// Resolve { position } → fixed positioning style fragment.
function toastPositionStyle(position) {
  if (position === 'top-right') return { top: 20, right: 20 };
  return { bottom: 20, right: 20 };
}

export function Toast({ toast, onDismiss, position = 'bottom-right' }) {
  if (!toast) return null;
  const kind = toast.kind || 'info';
  const borderColor =
    kind === 'critical' ? 'var(--text-critical)' :
    kind === 'warning'  ? 'var(--text-warning)' :
    kind === 'success'  ? 'var(--text-success)' :
                          'var(--text-info)';
  return (
    <div
      role="status"
      className="no-print"
      onClick={onDismiss}
      style={{
        position: 'fixed',
        ...toastPositionStyle(position),
        zIndex: 60,
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderLeft: `3px solid ${borderColor}`,
        borderRadius: 8,
        padding: '12px 16px',
        maxWidth: 380,
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        cursor: 'pointer',
      }}
    >
      <div className="text-sm" style={{ color: 'var(--text-primary)', lineHeight: 1.5 }}>
        {toast.text}
      </div>
    </div>
  );
}

// ── tone → DESIGN_SYSTEM color token ────────────────────────────────────
// Single source of truth for the live-parse RichToast palette. Keep
// aligned with src/lib/invoiceDiff.js #classToTone.
function toneToColors(tone) {
  switch (tone) {
    case 'red':
      return {
        eyebrow: 'var(--text-critical)',
        accent: 'var(--text-critical)',
        accentBg: 'color-mix(in srgb, var(--text-critical) 8%, transparent)',
      };
    case 'amber':
      return {
        eyebrow: 'var(--text-warning)',
        accent: 'var(--text-warning)',
        accentBg: 'color-mix(in srgb, var(--text-warning) 10%, transparent)',
      };
    case 'green':
      return {
        eyebrow: 'var(--text-success)',
        accent: 'var(--text-success)',
        accentBg: 'color-mix(in srgb, var(--text-success) 8%, transparent)',
      };
    case 'neutral':
    default:
      return {
        eyebrow: 'var(--text-secondary)',
        accent: 'var(--text-info)',
        accentBg: 'transparent',
      };
  }
}

// ── RichToast ───────────────────────────────────────────────────────────
// Multi-line live-event toast. Eyebrow (small caps tone-tinted) +
// headline (15/600 primary) + detail (13/400 secondary) + optional CTA
// link in the toast tail.
//
// Click anywhere on the body dismisses (same affordance as plain Toast).
// Click the CTA fires onCta AND dismisses (we don't want the toast lingering
// once the operator has acted).
export function RichToast({ toast, onDismiss, position = 'top-right', inline = false }) {
  if (!toast) return null;
  const tone = toast.tone || 'neutral';
  const colors = toneToColors(tone);

  // When `inline` is true (rendered inside <ToastQueue>) the parent
  // owns positioning — the toast itself is just a styled box. When
  // standalone, we apply position:fixed to anchor it to the viewport edge.
  const positioning = inline
    ? { position: 'relative' }
    : { position: 'fixed', ...toastPositionStyle(position), zIndex: 60 };

  return (
    <div
      role="status"
      className="no-print"
      style={{
        ...positioning,
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderLeft: `3px solid ${colors.accent}`,
        borderRadius: 10,
        padding: '12px 16px',
        minWidth: 320,
        maxWidth: 420,
        boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
        animation: 'toastSlideIn 220ms ease-out',
      }}
    >
      <div onClick={onDismiss} style={{ cursor: 'pointer' }}>
        {toast.eyebrow && (
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: colors.eyebrow,
              marginBottom: 4,
            }}
          >
            {toast.eyebrow}
          </div>
        )}
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.35 }}>
          {toast.headline}
        </div>
        {toast.detail && (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.5 }}>
            {toast.detail}
          </div>
        )}
      </div>
      {toast.ctaText && toast.onCta && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            toast.onCta();
            onDismiss?.();
          }}
          style={{
            marginTop: 10,
            padding: '6px 12px',
            background: colors.accentBg,
            color: colors.accent,
            border: `1px solid ${colors.accent}`,
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {toast.ctaText} →
        </button>
      )}
    </div>
  );
}

// ── ToastQueue ──────────────────────────────────────────────────────────
// Renders multiple toasts stacked with 8px gap from the anchor edge.
// Each toast must carry a stable `id` (string|number) — used as React key
// and passed to onDismiss. The queue is purely presentational; the host
// owns the array and is responsible for adding/removing entries.
//
// `render` lets the host swap the rendered toast component (defaults to
// RichToast). The default renderer expects toasts shaped for RichToast:
// { id, tone, eyebrow, headline, detail, ctaText?, onCta? }.
export function ToastQueue({ toasts, onDismiss, position = 'top-right', render }) {
  if (!toasts || toasts.length === 0) return null;
  const isTop = position === 'top-right';
  const renderToast = render || ((t, dismiss) => (
    <RichToast toast={t} onDismiss={dismiss} position={position} inline />
  ));
  // We render each toast as its own fixed element, offsetting via top/
  // bottom by index * (height + gap). Using a fixed approximate height
  // keeps the math simple — the toast itself doesn't need to know about
  // its siblings. 92px is a safe default for 2-line + CTA RichToast.
  const STEP = 92;
  return (
    <>
      {toasts.map((t, i) => {
        const offset = 20 + i * STEP;
        const positionStyle = isTop
          ? { top: offset, right: 20 }
          : { bottom: offset, right: 20 };
        return (
          <div key={t.id} style={{ position: 'fixed', ...positionStyle, zIndex: 60 }}>
            {renderToast(t, () => onDismiss?.(t.id))}
          </div>
        );
      })}
    </>
  );
}
